import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  Building2,
  Shield,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Server,
  Lock,
  Activity
} from 'lucide-react';

const RBICMSITFramework = () => {
  const frameworks = [
    { name: 'IT Governance', compliance: 96, status: 'compliant', findings: 2 },
    { name: 'Cyber Security', compliance: 94, status: 'compliant', findings: 3 },
    { name: 'IT Operations', compliance: 98, status: 'compliant', findings: 1 },
    { name: 'IS Audit', compliance: 92, status: 'in-progress', findings: 4 },
    { name: 'Cyber Crisis Management', compliance: 95, status: 'compliant', findings: 2 },
    { name: 'Cyber Resilience', compliance: 93, status: 'compliant', findings: 3 }
  ];

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          RBI CMS IT Framework
        </h1>
        <p className="text-gray-600">
          Cyber Security & IT Risk Management Framework Compliance
        </p>
      </div>

      <div className="p-8 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Building2 className="w-8 h-8 text-blue-600" />
                <Badge className="bg-green-100 text-green-700">96%</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">96%</p>
              <p className="text-sm text-gray-600 mt-1">Overall Compliance</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
                <Badge variant="secondary">Active</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">12</p>
              <p className="text-sm text-gray-600 mt-1">Controls Implemented</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <AlertTriangle className="w-8 h-8 text-yellow-600" />
                <Badge className="bg-yellow-100 text-yellow-700">Review</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">15</p>
              <p className="text-sm text-gray-600 mt-1">Findings</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Activity className="w-8 h-8 text-purple-600" />
                <Badge variant="secondary">Next: Jun 2026</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">Q2</p>
              <p className="text-sm text-gray-600 mt-1">Next Assessment</p>
            </CardContent>
          </Card>
        </div>

        {/* Framework Compliance */}
        <Card>
          <CardHeader>
            <CardTitle>CMS IT Framework Components</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {frameworks.map((framework, index) => (
                <div key={framework.name || `framework-${index}`} className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{framework.name}</h3>
                        <p className="text-xs text-gray-600">{framework.findings} findings identified</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge className={framework.status === 'compliant' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                        {framework.status}
                      </Badge>
                      <span className="text-lg font-bold text-gray-900">{framework.compliance}%</span>
                    </div>
                  </div>
                  <Progress value={framework.compliance} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Audits & Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Audit Findings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 mb-1">Critical: MFA Implementation Gap</p>
                      <p className="text-xs text-gray-600 mb-2">Multi-factor authentication not enforced for privileged users</p>
                      <Badge variant="destructive" className="text-xs">Action Required</Badge>
                    </div>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 mb-1">Medium: Vulnerability Assessment</p>
                      <p className="text-xs text-gray-600 mb-2">Quarterly VA/PT schedule needs strengthening</p>
                      <Badge className="bg-yellow-100 text-yellow-700 text-xs">In Progress</Badge>
                    </div>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 mb-1">Resolved: Incident Response Plan</p>
                      <p className="text-xs text-gray-600 mb-2">Cyber crisis management plan updated and tested</p>
                      <Badge className="bg-green-100 text-green-700 text-xs">Closed</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Key Controls Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Lock className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-600">Access Control</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">98%</span>
                  </div>
                  <Progress value={98} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Server className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-600">Infrastructure Security</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">95%</span>
                  </div>
                  <Progress value={95} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-600">Data Protection</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">97%</span>
                  </div>
                  <Progress value={97} className="h-2" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Activity className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-600">Monitoring & Logging</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">94%</span>
                  </div>
                  <Progress value={94} className="h-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RBICMSITFramework;
